import * as React from 'react';
import '../Components/ContactForm.css';
import { Icon } from '../Icons/Icon';
import FadeIn from '../Animations/FadeIn';
import { CheckoutLoader } from '../Components/CheckoutLoader';
import { ContactinformationForm, PaymentMethodKeys, SwishForm, SwishPaymentRequestForm } from '../Types/PaymentFormTypes';
import { PaymentMethod } from '../Components/PaymentMethod';
import { PromoCodeField } from '../Components/PromoCodeField';
import { DataService } from '../DataService/DataService';
import { usePaymentHub } from '../DataService/PaymentHubService';
import { useLocation } from 'react-router-dom';
import { StripeCheckoutWrapper } from '../Components/StripeCheckout';
import { CheckoutOrderRequest, CheckoutOrderResponse, CheckoutState } from '../Types/CartTypes';
import { useUser } from '../DataService/UserContext';
import { normalizeSwishPayerAlias } from '../Utils/swishPhone';
import './PaymentForm.css';
import { Card, CardContent } from '../Components/Card';

const emptyPaymentForm: ContactinformationForm = {
  form: {
    firstName: { value: '', isValid: false },
    lastName: { value: '', isValid: false },
    phone: { value: '', isValid: false },
    email: { value: '', isValid: false },
    address: { value: '', isValid: false },
    city: { value: '', isValid: false },
    postalCode: { value: '', isValid: false },
    promoCode: { value: '', isValid: true },
  },
  fullFilled: false,
};

export const PaymentForm = () => {
  const { state } = useLocation();
  const checkoutState = state as CheckoutState | undefined;
  const { user } = useUser();

  const [step, setStep] = React.useState<'paymentconfirmation' | 'validation-swish' | 'validation-stripe' | 'success' | 'error'>('paymentconfirmation');
  const [paymentForm, setPaymentForm] = React.useState<ContactinformationForm>(emptyPaymentForm);
  const [swishForm, setSwishForm] = React.useState<SwishForm>({
    phoneNumber: '',
    acceptedTermsOfService: false,
    priceToPay: '',
    fullFilled: false,
  });
  const [paymentId, setPaymentId] = React.useState<string | null>(null);
  const [checkoutOrder, setCheckoutOrder] = React.useState<CheckoutOrderResponse | null>(null);
  const [checkoutError, setCheckoutError] = React.useState<string | null>(null);
  const [subscriptionClientSecret, setSubscriptionClientSecret] = React.useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = React.useState<string | null>(null);
  const isRotProgramFlow = Boolean(
    checkoutState?.items.some(item =>
      item.type === 'program' &&
      String(item.metadata?.programKey ?? item.referenceId).toLowerCase() === 'rot'
    )
  );

  const paymentStatus = usePaymentHub(paymentId ?? '');
  const isIncludedBookingFlow = Boolean(
    checkoutState?.items.length &&
    checkoutState.items.every(item => item.type === 'booking') &&
    checkoutState.totalAmount === 0
  );

  React.useEffect(() => {
    if (!checkoutState?.contact) return;

    setPaymentForm(prev => ({
      ...prev,
      form: {
        ...prev.form,
        firstName: { value: checkoutState.contact.firstName, isValid: checkoutState.contact.firstName.length > 1 },
        lastName: { value: checkoutState.contact.lastName, isValid: checkoutState.contact.lastName.length > 1 },
        phone: { value: checkoutState.contact.phone, isValid: checkoutState.contact.phone.length > 0 },
        email: { value: checkoutState.contact.email, isValid: checkoutState.contact.email.length > 0 },
        address: { value: checkoutState.contact.address, isValid: checkoutState.contact.address.length > 0 },
        city: { value: checkoutState.contact.city, isValid: checkoutState.contact.city.length > 0 },
        postalCode: { value: checkoutState.contact.postalCode, isValid: checkoutState.contact.postalCode.length > 0 },
      },
    }));

    setSwishForm(prev => ({
      ...prev,
      phoneNumber: checkoutState.contact.phone,
      priceToPay: checkoutState.totalAmount.toFixed(2),
    }));
  }, [checkoutState]);

  React.useEffect(() => {
    if (paymentStatus === 'PAID') {
      setStep('success');
    }
  }, [paymentStatus]);

  const buildCheckoutRequest = React.useCallback((paymentMethod: string): CheckoutOrderRequest | null => {
    if (!checkoutState) return null;

    return {
      paymentMethod,
      currency: 'SEK',
      applicationUserId: user?.id,
      contact: checkoutState.contact,
      promoCode: paymentForm.form.promoCode?.value?.trim() || undefined,
      captchaToken: isRotProgramFlow ? captchaToken ?? undefined : undefined,
      items: checkoutState.items.map(item => {
        const slots = item.metadata?.slots;
        const programKey = item.metadata?.programKey as string | undefined;
        const programStartDate = item.metadata?.programStartDate as string | undefined;
        const registrationFeePaid = item.metadata?.registrationFeePaid as boolean | undefined;
        const finalPaymentDueAt = item.metadata?.finalPaymentDueAt as string | undefined;
        const membershipSnapshotJson = item.metadata?.pricingSnapshotJson as string | undefined;

        return {
          itemType: item.type,
          referenceId: item.referenceId,
          title: item.title,
          courseId: item.courseId,
          programKey,
          programStartDate,
          registrationFeePaid,
          finalPaymentDueAt,
          membershipSnapshotJson,
          unitPrice: item.price,
          quantity: item.quantity,
          bookingSlots: Array.isArray(slots) ? slots : undefined,
          metadataJson: item.metadata ? JSON.stringify(item.metadata) : undefined,
        };
      }),
    };
  }, [checkoutState, user?.id, paymentForm.form.promoCode?.value, captchaToken, isRotProgramFlow]);

  const ensureCheckoutOrder = React.useCallback(async (paymentMethod: 'swish' | 'stripe' | 'included') => {
    if (checkoutOrder) return checkoutOrder;

    const request = buildCheckoutRequest(paymentMethod);
    if (!request) {
      throw new Error('Missing checkout state');
    }

    const order = await DataService.PaymentService.CreateCheckoutSession(request);
    setCheckoutOrder(order);
    return order;
  }, [buildCheckoutRequest, checkoutOrder]);

  const resolveMembershipStripePriceId = React.useCallback((item?: CheckoutState['items'][number]) => {
    if (!item) return null;

    const metadataPriceId = item.metadata?.stripePriceId as string | undefined;
    if (metadataPriceId) return metadataPriceId;

    const plan = String(item.metadata?.plan ?? item.referenceId).toLowerCase();
    if (plan === 'premium' || plan === 'vip') {
      return process.env.REACT_APP_STRIPE_PRICE_GROW_PREMIUM ?? null;
    }

    return process.env.REACT_APP_STRIPE_PRICE_GROW_BASIC ?? null;
  }, []);

  React.useEffect(() => {
    if (!checkoutState || !isIncludedBookingFlow) return;

    const run = async () => {
      try {
        await ensureCheckoutOrder('included');
        setStep('success');
      } catch (error) {
        console.error(error);
        setCheckoutError('Kunde inte slutföra den inkluderade bokningen.');
        setStep('error');
      }
    };

    void run();
  }, [checkoutState, isIncludedBookingFlow, ensureCheckoutOrder]);

  const verifyPromotionCode = async (): Promise<void> => {
    // Validation is handled inside PromoCodeField.
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setPaymentForm(prev => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: {
          value: type === 'checkbox' ? String((e.target as HTMLInputElement).checked) : value,
          isValid: value.length > 2,
        },
      },
    }));
  };

  const onConfirmation = async (method: PaymentMethodKeys, policyAccepted: boolean, providedCaptchaToken: string | null) => {
    if (!policyAccepted || !method) return;

    if (isRotProgramFlow) {
      setCaptchaToken(providedCaptchaToken);
    }

    try {
      if (method === 'swish') {
        setSwishForm(prev => ({ ...prev, fullFilled: true, acceptedTermsOfService: true }));
        setStep('validation-swish');

        const normalizedPayerAlias = normalizeSwishPayerAlias(swishForm.phoneNumber);
        if (!normalizedPayerAlias.value) {
          throw new Error(normalizedPayerAlias.error ?? 'Ogiltigt Swish-nummer.');
        }

        const order = await ensureCheckoutOrder('swish');
        const selectedItem = checkoutState?.items?.[0];

        const requestDto: SwishPaymentRequestForm = {
          payerAlias: normalizedPayerAlias.value,
          payeeAlias: '1233317930',
          amount: swishForm.priceToPay || String(checkoutState?.totalAmount ?? 0),
          currency: 'SEK',
          message: 'Payment',
          productType: selectedItem?.type === 'booking'
            ? 'booking'
            : selectedItem?.type === 'course'
              ? 'course'
              : selectedItem?.type === 'program'
                ? 'program'
                : 'membership',
          courseId: selectedItem?.courseId ?? null,
          programKey: selectedItem?.metadata?.programKey as string | undefined,
          programStartDate: selectedItem?.metadata?.programStartDate as string | undefined,
          registrationFeePaid: selectedItem?.metadata?.registrationFeePaid as boolean | undefined,
          finalPaymentDueAt: selectedItem?.metadata?.finalPaymentDueAt as string | undefined,
          membershipSnapshotJson: selectedItem?.metadata?.pricingSnapshotJson as string | undefined,
          orderId: order.orderId,
          firstName: checkoutState?.contact.firstName ?? '',
          lastName: checkoutState?.contact.lastName ?? '',
          phone: checkoutState?.contact.phone ?? '',
          email: checkoutState?.contact.email ?? '',
          address: checkoutState?.contact.address ?? '',
          city: checkoutState?.contact.city ?? '',
          postalCode: checkoutState?.contact.postalCode ?? '',
          promoCode: paymentForm.form.promoCode?.value,
        };

        const swishPaymentInitiated = await DataService.PaymentService.SendSwishPaymentRequest(requestDto);
        setPaymentId(swishPaymentInitiated);
      }

      if (method === 'stripe') {
        const selectedItem = checkoutState?.items?.[0];

        if (selectedItem?.type === 'membership') {
          const priceId = resolveMembershipStripePriceId(selectedItem);
          if (!priceId) {
            throw new Error('Saknar Stripe PriceId för medlemskap.');
          }

          const subscription = await DataService.PaymentService.CreateSubscription({
            email: checkoutState?.contact.email ?? '',
            priceId,
            applicationUserId: user?.id,
            planKey: String(selectedItem?.metadata?.plan ?? selectedItem?.referenceId ?? '').toLowerCase() === 'premium' || String(selectedItem?.metadata?.plan ?? selectedItem?.referenceId ?? '').toLowerCase() === 'vip'
              ? 'premium'
              : 'basic',
            membershipSnapshotJson: selectedItem?.metadata?.pricingSnapshotJson as string | undefined,
          });

          setCheckoutOrder(null);
          setSubscriptionClientSecret(subscription.clientSecret);
          setStep('validation-stripe');
          return;
        }

        const order = await ensureCheckoutOrder('stripe');
        setCheckoutOrder(order);
        setSubscriptionClientSecret(null);
        setStep('validation-stripe');
      }

    } catch (error) {
      console.error(error);
      setCheckoutError(error instanceof Error ? error.message : 'Kunde inte skapa order eller starta betalning.');
      setStep('error');
    }
  };

  if (!checkoutState) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
        <p>Kunde inte hitta någon kassa att fortsätta från.</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'white' }}>
      <div style={{ maxWidth: 960, margin: '56px auto', padding: '0 24px' }}>
        {step !== 'success' && step !== 'error' && (
          <Card className="payment-form-shell" disableHover>
            <CardContent className="payment-form-shell-content">
              <FadeIn>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                  <h2 className="payment-form-title">Kassan</h2>
                  <Icon.CheckoutPayment width={80} height={80} />
                  <p className="payment-form-subtitle">Kontaktuppgifterna kommer från varukorgen. Kampanjkod är det enda du behöver fylla i här.</p>
                </div>
              </FadeIn>

              <div className="payment-form-stack">
                <section className="payment-form-section">
                  <h3 className="payment-form-section-title">Kontaktuppgifter</h3>
                  <div className="payment-form-summary-grid">
                    <div><strong>Namn:</strong> {checkoutState.contact.firstName} {checkoutState.contact.lastName}</div>
                    <div><strong>E-post:</strong> {checkoutState.contact.email}</div>
                    <div><strong>Telefon:</strong> {checkoutState.contact.phone}</div>
                    <div><strong>Adress:</strong> {checkoutState.contact.address}, {checkoutState.contact.postalCode} {checkoutState.contact.city}</div>
                  </div>
                </section>

                <section className="payment-form-section">
                  <PromoCodeField
                    paymentForm={paymentForm}
                    handleChange={handleChange}
                    onPromoCodeValidationCallback={verifyPromotionCode}
                  />
                </section>

                <section className="payment-form-section payment-form-section--last">
                <PaymentMethod onConfirmation={onConfirmation} swishForm={swishForm} requiresCaptcha={isRotProgramFlow} />
              </section>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 'validation-swish' && paymentForm.fullFilled && swishForm.fullFilled && (
          <CheckoutLoader
            disable
            message={
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', gap: '0.5rem', margin: '15px 50px' }}>
                <Icon.SwishIcon />
                Öppna din swish app för att slutföra betalning
              </div>
            }
          />
        )}

        {step === 'validation-stripe' && (
          <StripeCheckoutWrapper
            amount={checkoutState.totalAmount ?? 300}
            paymentReference={checkoutOrder?.paymentReference ?? ''}
            clientSecret={subscriptionClientSecret ?? undefined}
            onSuccess={() => setStep('success')}
          />
        )}

        {step === 'success' && (
          <Card className="payment-form-shell" disableHover style={{ border: isRotProgramFlow ? '1px solid #e8b7ba' : undefined, background: isRotProgramFlow ? '#fff9f9' : undefined }}>
            <CardContent className="payment-form-shell-content" style={{ textAlign: 'center' }}>
              <div style={{ display: 'grid', gap: 12, justifyItems: 'center' }}>
                <Icon.CheckMarkCircle />
                <h2 className="payment-form-title">
                  {isRotProgramFlow
                    ? 'Tack, din ROT-anmälan är registrerad'
                    : 'Tack, ditt köp är klart'}
                </h2>
                <p className="payment-form-subtitle" style={{ maxWidth: 560 }}>
                  {isRotProgramFlow
                    ? 'Kolla din inkorg efter bekräftelsen. Om du är ny kund har vi skickat instruktioner för att sätta ditt lösenord. När betalningen är registrerad får du nästa steg för ROT-programmet.'
                    : 'Du får en bekräftelse i din inkorg inom kort. Om du inte ser mailet, kontrollera skräpposten.'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        {step === 'error' && checkoutError && <p style={{ color: 'red' }}>{checkoutError}</p>}
      </div>
    </div>
  );
};
